import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getWorkspaceMetadata } from '../workspaceMetadata/workspaceMetadataApi'

export const POLLING_CHECK_ACTIONS = {
    PROCESSING: 'processing',
    CREATION: 'creation',
}

export const useProcessingPolling = (
    checkAction,
    isPolling,
    setIsPolling,
    inbox,
    onFinishPolling,
    MAX_POLLING_COUNT = 150,
    onReachMiddleOfPolling,
    workspaceID = null // Optional workspaceID to override the current workspace,
) => {
    const [count, setCount] = useState(0)
    const [middleOfPollingReached, setMiddleOfPollingReached] = useState(false)

    const { data: workspaceData, isFetching: fetchingWorkspaceData } = useQuery({
        queryKey: ['processingPolling', inbox?.inboxID || inbox?.inboxName, workspaceID],
        queryFn: () => {
            return getWorkspaceMetadata(true, workspaceID, true) // Pass isPolling=true to prevent interceptor override
        },
        enabled: count <= MAX_POLLING_COUNT && isPolling, // Removed inbox requirement
        refetchInterval: isPolling ? 2000 : false,
    })

    useEffect(() => {
        if (isPolling) setCount(0)
    }, [isPolling])

    useEffect(() => {
        if (!fetchingWorkspaceData && isPolling) {
            if (
                onReachMiddleOfPolling &&
                count >= Math.floor(MAX_POLLING_COUNT / 2) &&
                !middleOfPollingReached
            ) {
                onReachMiddleOfPolling()
                setMiddleOfPollingReached(true)
            } else if (count >= MAX_POLLING_COUNT) {
                onFinishPolling({ error: 'Request timed out' })
                setIsPolling(false)
                setCount(0)
            } else {
                setCount((prev) => prev + 1)
            }
        }
    }, [fetchingWorkspaceData])

    useEffect(() => {
        if (!workspaceData || !isPolling) return

        // If inbox is provided, check for specific inbox completion
        if (inbox) {
            switch (checkAction) {
                case POLLING_CHECK_ACTIONS.PROCESSING:
                    const createdInbox = workspaceData.inboxes.find(
                        (i) => i.inboxID === inbox.inboxID
                    )
                    if (
                        count > 0 &&
                        createdInbox &&
                        createdInbox.tables?.some((table) => table.reportSeriesTableID)
                    ) {
                        onFinishPolling()
                        setIsPolling(false)
                        setCount(0)
                    }
                    break
                case POLLING_CHECK_ACTIONS.CREATION:
                    const searchInbox = workspaceData.inboxes.find(
                        (i) => i.inboxName === inbox.inboxName || i.label === inbox.inboxName
                    )
                    if (count > 0 && searchInbox) {
                        onFinishPolling()
                        setIsPolling(false)
                        setCount(0)
                    }
                    break
                default:
                    return
            }
        }
    }, [count])

    return {
        workspaceData,
        fetchingWorkspaceData,
    }
}
